import { useMemo, useState } from 'react';
import { ButtonGrey, ButtonWhite } from '../shared/Buttons';
import { Input } from '../shared/Input';
import { FormRow, InputRow } from '../shared/InputRow';
import { Modal, ModalProps } from '../shared/Modal';
import { convertObjectToOptions, getOptionList } from '../../scripts/scripts';
import { Select } from '../shared/Select';
import { TixBought, TixSale, TixSecondaryName, TixSecondaryTypes } from '../../types/TixTypes';
import toast from 'react-hot-toast';
import { useApiMutation } from '../../scripts/api/api_v2';
import { GETEventResponseBase, PatchSalePayload } from '../../types/TixAPITypes';
import { FlexCol } from '../shared/Flex';
import { Text } from '../shared/Text';

export function ModalPatchSale({
	closeModal,
	seatIds,
	event,
	onSaleAdded,
}: ModalProps & {
	seatIds: TixBought['id'][];
	event: GETEventResponseBase;
	onSaleAdded: (sale: TixSale) => void;
}) {
	const [saving, setSaving] = useState<boolean>(false);
	const [formState, setFormState] = useState({
		secondary: '',
		reference: '',
	});

	const { fetchData: postSaleAPI } = useApiMutation('tix.patch-sale');

	const onChange = (name: keyof typeof formState, value: string | number) => {
		setFormState((state) => ({ ...state, [name]: value }));
	};

	const onClickSave = async () => {
		try {
			setSaving(true);

			const saleBody: PatchSalePayload = {
				tixId: seatIds,
				eventId: event.id,
				secondary: formState.secondary as TixSecondaryTypes,
				reference: formState.reference,
			};

			const savedSaleRes = await postSaleAPI({
				body: saleBody,
			});
			if (!savedSaleRes?.success) {
				toast.error(savedSaleRes?.error ?? 'Failed to add seats to sale!');
				return;
			}
			onSaleAdded(savedSaleRes.data);
			toast.success(`Added seats to sale ${formState.reference}!`);
			closeModal();
		} catch (err) {
			console.error(err);
		} finally {
			setSaving(false);
		}
	};

	const checkInputs = () => {
		if (!formState['secondary']) return false;
		if (!formState['reference']) return false;
		return true;
	};

	const seatInfo = useMemo(() => {
		const idSet = new Set(seatIds);
		const seats: TixBought[] = Object.values(event.seats).filter((b) => idSet.has(b.id));
		return seats;
	}, [seatIds, event]);

	return (
		<Modal title="Add seats to sale" closeModal={closeModal}>
			<FormRow>
				<FlexCol style={{ rowGap: '0.5rem' }}>
					<Text $color="text_desc_main" $size="1.6rem" $weight="600">
						Selected Seats
					</Text>
					{seatInfo.map((seat, seatIndex) => (
						<Text
							$color="text_desc_alt"
							$size="1.3rem"
							$weight="400"
							key={`add_sale_seat_${seatIndex}`}
						>{`${seat.section} - Row ${seat.row}- Seat ${seat.seat}`}</Text>
					))}
				</FlexCol>
			</FormRow>

			<FormRow>
				<InputRow label="Secondary">
					<Select
						value={formState['secondary']}
						onChange={(v) => onChange('secondary', v)}
						options={getOptionList('Secondary', convertObjectToOptions(TixSecondaryName))}
					/>
				</InputRow>
			</FormRow>

			<FormRow>
				<InputRow label="Reference">
					<Input
						value={formState['reference']}
						onChange={(v) => onChange('reference', v)}
						placeholder="# 123456/UK1"
					/>
				</InputRow>
			</FormRow>

			<FormRow>
				<ButtonGrey onClick={closeModal}>Cancel</ButtonGrey>
				<ButtonWhite disabled={!checkInputs() || saving} onClick={onClickSave}>
					Add Seats
				</ButtonWhite>
			</FormRow>
		</Modal>
	);
}
